import * as Sentry from '@sentry/react';
import axios from 'axios'; // Make sure axios is imported

// Define the environment variables type to avoid TypeScript errors
declare global {
  interface ImportMeta {
    env: {
      VITE_SENTRY_DSN: string;
      VITE_SENTRY_ENVIRONMENT: string;
    };
  }
}

export function initSentry() {
  // Check if we have a DSN and the environment is not 'development'
  // This ensures Sentry is enabled in staging and production
  if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_SENTRY_ENVIRONMENT !== 'development') {
    try {
      // Get available integrations - explicitly NOT including profiling to avoid document policy violations
      const integrations = [
        // Add browser tracing integration
        // @ts-ignore - Ignoring type issues with Sentry integrations
        Sentry.browserTracingIntegration(),
      ];

      Sentry.init({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
        integrations,
        // Tracing
        tracesSampleRate: 1.0,
        // Profiling disabled to avoid document policy violations
        profilesSampleRate: 0.0,
        // Completely disable trace propagation to avoid CORS issues
        tracePropagationTargets: [],
      });

      // Set up Axios interceptor to capture API errors
      setupAxiosErrorCapture();

    } catch (error) {
      console.error('Failed to initialize Sentry:', error);
    }
  }
}

// Function to set up Axios interceptor for capturing API errors
function setupAxiosErrorCapture() {
  // Check if axios is available
  if (typeof axios !== 'undefined') {
    // Add a response interceptor
    axios.interceptors.response.use(
      (response) => {
        // Add breadcrumb for successful responses
        Sentry.addBreadcrumb({
          category: 'xhr',
          message: `${response.config.method?.toUpperCase()} ${response.config.url}`,
          data: {
            status: response.status,
            statusText: response.statusText,
          },
          level: 'info',
        });
        return response;
      },
      (error) => {
        // Only capture 4XX errors
        if (error.response && error.response.status >= 400 && error.response.status < 500) {
          // Add detailed breadcrumb
          Sentry.addBreadcrumb({
            category: 'xhr',
            message: `API Error: ${error.config.method?.toUpperCase()} ${error.config.url}`,
            data: {
              status: error.response.status,
              statusText: error.response.statusText,
              url: error.config.url,
              method: error.config.method,
              // Don't include sensitive data like headers or request body
            },
            level: 'error',
          });

          // Capture the error with Sentry
          Sentry.captureException(new Error(`API ${error.config.method?.toUpperCase()} ${error.response.status} Error: ${error.response.statusText}`), {
            tags: {
              api_status: error.response.status,
              api_endpoint: error.config.url,
            },
            extra: {
              url: error.config.url,
              method: error.config.method,
              status: error.response.status,
              statusText: error.response.statusText,
            },
          });
        }
        return Promise.reject(error);
      }
    );
  }
} 